import { PlatformIcon } from '@/components/PlatformIcon';
import MouseFollowTooltip from '@/components/tooltips/MouseFollowTooltip';
import { useFilterContext } from '@/context';
import { useMouseFollowTooltip } from '@/hooks/useMouseFollowTooltip';
import { Platform } from '@/types';
import { SocialProfile, SupportedSocialNetwork } from '@/types/api/social-profiles';

function getChannelLabelByType(type: SupportedSocialNetwork, count: number) {
  switch (type) {
    case Platform.Acast:
      return count > 1 ? `${count} audio podcasts` : '1 audio podcast';
    case Platform.YouTube:
      return count > 1 ? `${count} YouTube channels` : '1 YouTube channel';
    case Platform.TikTok:
      return count > 1 ? `${count} TikTok channels` : '1 TikTok channel';
    case Platform.Instagram:
      return count > 1 ? `${count} Instagram accounts` : '1 Instagram account';
    default:
      return '';
  }
}

const TooltipContent = ({
  profiles,
  type,
}: {
  profiles: SocialProfile[];
  type: SupportedSocialNetwork;
}) => {
  return (
    <div>
      <h4 className="mb-2 font-medium text-sm">{getChannelLabelByType(type, profiles.length)}</h4>
      <ul className="list-outside list-disc ml-6">
        {profiles.map((profile) => (
          <li className="mb-1 text-sm" key={`${type}-${profile.handle}`}>
            {profile.handle}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SocialChannelProfileItem = ({
  type,
  profiles,
}: {
  type: SupportedSocialNetwork;
  profiles: SocialProfile[];
}) => {
  const hasProfiles = profiles.length > 0;
  const { isOpen: isTooltipOpen, point: tooltipPoint, containerProps: socialRowContainerProps } =
    useMouseFollowTooltip(hasProfiles);
  const { filters } = useFilterContext();
  const isShowFilterEnabled = filters.selectedShows.length > 0;
  if (profiles.length === 0) {
    return (
      <div className="flex min-w-0 max-w-full flex-row items-center overflow-hidden">
        <PlatformIcon name={type} />
        <div className="flex min-w-0 flex-1 flex-row items-center overflow-hidden">
          <p className="min-w-0 max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-textMedGrey font-light sm:max-w-[150px] lg:max-w-[150px] xl:max-w-[200px]">
            n/a
          </p>
        </div>
      </div>
    );
  }
  return (
    <div {...socialRowContainerProps} className="flex min-w-0 max-w-full flex-row items-center overflow-hidden">
      <PlatformIcon name={type} className="mr-1" />
      <div className="flex min-w-0 flex-1 flex-row items-center overflow-hidden">
        {!isShowFilterEnabled && (
          <h4 className="mb-2 font-semibold">{getChannelLabelByType(type, profiles.length)}</h4>
        )}
        <p className="min-w-0 max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-textMedGrey font-light sm:max-w-[150px] lg:max-w-[150px] xl:max-w-[200px]">
          {profiles.map(
            (profile, index) => profile.handle + (index < profiles.length - 1 ? ' • ' : '')
          )}
        </p>
      </div>
      <MouseFollowTooltip
        isOpen={isTooltipOpen}
        clientX={tooltipPoint.x}
        clientY={tooltipPoint.y}
        zIndex={50}
      >
        <TooltipContent profiles={profiles} type={type} />
      </MouseFollowTooltip>
    </div>
  );
};
